import { ref, watch } from 'vue'

import { useRoute, useNuxtApp } from '#app'
import { useI18n } from '#imports'
import ToastSwUpdate from '@/components/ToastSwUpdate.vue'

const PERIODIC_CHECK_MS = 30 * 60 * 1000
const RELOAD_DELAY_MS = 100
const UPDATE_FEEDBACK_MS = 3500

export const usePwaUpdate = () => {
  const nuxtApp = useNuxtApp()

  // Dev / SSR no-op：
  // - nuxt.config 內 pwa.devOptions.enabled = false，正式才會有真正的 SW
  // - 但 registerSW 仍會載入 virtual:pwa-register stub，搭配 HMR 全頁重整可能造成
  //   onNeedRefresh 不預期觸發 forceReload（iOS WebClip zombie loop 偵測），導致
  //   開發過程中頁面不斷重整。Dev/SSR 直接 no-op，正式行為完全不變。
  // - 走 nuxtApp._pwaUpdateContext 單例避免每次呼叫都建立新的 ref(false) + 閉包，
  //   對齊 docs/standards/ssr-memory-safety.md 規範。
  if (import.meta.server || import.meta.dev) {
    if (!nuxtApp._pwaUpdateContext) {
      nuxtApp._pwaUpdateContext = {
        needsRefresh: ref(false),
        registerPwaUpdate: () => {},
        clearAllCaches: () => Promise.resolve(false),
        forceReload: () => {},
        cleanup: () => {},
      }
    }
    return nuxtApp._pwaUpdateContext
  }

  if (!nuxtApp._pwaUpdateContext) {
    const route = useRoute()
    const { t } = useI18n()
    const { $toast } = nuxtApp

    const needsRefresh = ref(false)
    let updateSW = null
    let updateInterval = null
    let registrationRef = null
    const stopWatchers = []

    const isGamePage = () => route.path.startsWith('/game')

    const showUpdateToast = () => {
      $toast.info(ToastSwUpdate, {
        contentProps: {
          message: t('description.new_version'),
          updatingMessage: t('common.updating'),
          completeMessage: t('common.update_complete'),
          buttonText: t('common.update'),
          reloadText: t('common.reload'),
          onUpdate: () => {
            if (!updateSW) return Promise.resolve()

            sessionStorage.setItem('pwa-update-in-progress', 'true')

            return new Promise((resolve) => {
              setTimeout(() => {
                resolve()
              }, UPDATE_FEEDBACK_MS)
            })
          },
          onReload: () => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.getRegistration().then((reg) => {
                if (reg && reg.waiting) {
                  reg.waiting.postMessage({ type: 'SKIP_WAITING' })
                  reg.waiting.postMessage({ type: 'skipWaiting' })
                }
              })
            }

            setTimeout(() => {
              const currentUrl = new URL(window.location.href)
              currentUrl.searchParams.set('v', Date.now().toString())
              window.location.href = currentUrl.toString()
            }, RELOAD_DELAY_MS)
          },
        },
        expandCustomProps: true,
        autoClose: 30000,
        closeOnClick: false,
      })
    }

    const clearAllCaches = async () => {
      if (typeof window === 'undefined' || !('caches' in window)) return false

      try {
        const cacheNames = await caches.keys()
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
        console.log('[PWA] All caches cleared')
        return true
      } catch (error) {
        console.error('[PWA] Failed to clear caches:', error)
        return false
      }
    }

    const forceReload = async () => {
      await clearAllCaches()
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      }
      window.location.reload()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && registrationRef) {
        console.log('[PWA] Checking for updates: visibility change')
        registrationRef.update().catch((err) => console.error('[PWA] Failed to check for updates:', err))
      }
    }

    const handleOnline = () => {
      if (!registrationRef) return
      console.log('[PWA] Checking for updates: online')
      registrationRef.update().catch((err) => console.error('[PWA] Failed to check for updates:', err))
    }

    const startWatchers = (registration) => {
      registrationRef = registration

      updateInterval = setInterval(() => {
        console.log('[PWA] Checking for updates: periodic')
        registration.update().catch((err) => console.error('[PWA] Failed to check for updates:', err))
      }, PERIODIC_CHECK_MS)

      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('online', handleOnline)
    }

    const registerPwaUpdate = async () => {
      try {
        const { registerSW } = await import('virtual:pwa-register')

        updateSW = registerSW({
          onNeedRefresh() {
            if (sessionStorage.getItem('pwa-update-in-progress') === 'true') {
              sessionStorage.removeItem('pwa-update-in-progress')
              console.error('[PWA] Detected iOS WebClip Zombie Worker loop!!! Forcing hard reload...')
              forceReload()
              return
            }

            needsRefresh.value = true
            if (!isGamePage()) {
              showUpdateToast()
            }
          },
          onOfflineReady() {
            console.log('[PWA] App ready to work offline')
          },
          onRegistered(registration) {
            console.log('[PWA] SW Registered:', registration)
            if (!registration) return
            startWatchers(registration)
          },
          onRegisterError(error) {
            console.error('[PWA] SW registration error:', error)
            clearAllCaches()
          },
        })
      } catch (error) {
        console.error('[PWA] Failed to register SW:', error)
        clearAllCaches()
      }
    }

    stopWatchers.push(
      watch(
        () => route.path,
        (newPath) => {
          if (!newPath.startsWith('/game') && needsRefresh.value) {
            showUpdateToast()
          }
        }
      )
    )

    const cleanup = () => {
      if (updateInterval) {
        clearInterval(updateInterval)
        updateInterval = null
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
      stopWatchers.forEach((stop) => stop?.())
      stopWatchers.length = 0
      registrationRef = null
    }

    nuxtApp._pwaUpdateContext = {
      needsRefresh,
      registerPwaUpdate,
      clearAllCaches,
      forceReload,
      cleanup,
    }
  }

  return nuxtApp._pwaUpdateContext
}
